import { computed, type ComputedRef, type Ref, type TemplateRef } from 'vue'
import type {
    CustomerRegistrationInput,
    ProductAnalyticsFragment,
    RingBuilderResolveExistingCustomerMutation,
    RingBuilderResolveNewCustomerMutation,
    RingSizeInput,
} from '@/graphql/api'
import { RingBuilderResolveMode } from '@/graphql/api'

import { createResponseAndPath, type ResponseAndPath } from '@/helpers/errors'
import { useHandleAddToCartSuccess } from '../../composables/useHandleAddToCartSuccess'
import { useEngagementRingGetInput } from './useEngagementRingGetInput'
import { useCountryPlugin } from '@/plugins/country.ts'
import { useTranslator } from '@/i18n'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useRingBuilderResolveExistingCustomerMutation } from '@/graphql/api/mutations/ringBuilderResolveExistingCustomer.mutation.generated.ts'
import { useRingBuilderResolveNewCustomerMutation } from '@/graphql/api/mutations/ringBuilderResolveNewCustomer.mutation.generated.ts'
import type { RingBuilder } from '@/plugins/ring-builder.ts'
import type AuthFormInModal from '@/components/user/auth/AuthFormInModal.vue'
import { ROUTE_CART } from '@/router'

export interface EngagementRingAddToCart {
    addToCartNewCustomer: (successCallback: () => void) => void
    addToCartErrors: readonly [
        ResponseAndPath<RingBuilderResolveExistingCustomerMutation>,
        ResponseAndPath<RingBuilderResolveNewCustomerMutation>,
    ]
    addToCartExistingCustomer(): void
    fetchingAddToCart: ComputedRef<boolean>
}

export const useEngagementRingAddToCart = (
    ring: Readonly<Ref<ProductAnalyticsFragment | undefined | null>>,
    ringBuilder: Readonly<Ref<RingBuilder | undefined>>,
    centreStoneUri: Readonly<Ref<string | null>>,
    engraving: Readonly<Ref<string>>,
    customerRegistrationInput: Readonly<
        Ref<CustomerRegistrationInput | undefined>
    >,
    handleRingSizeNotSelected: () => RingSizeInput | null,
    authForm: TemplateRef<InstanceType<typeof AuthFormInModal>>
): EngagementRingAddToCart => {
    const currency = useCurrencyInput()
    const { countryCode } = useCountryPlugin()
    const { locale } = useTranslator()

    const isAdmin = useIsAdmin()

    const { getInput } = useEngagementRingGetInput(
        centreStoneUri,
        engraving,
        handleRingSizeNotSelected
    )

    const { prepare } = useHandleAddToCartSuccess(ring, ringBuilder)

    // Existing customer
    const responseExistingCustomer =
        useRingBuilderResolveExistingCustomerMutation()

    const {
        executeMutation: mutationExistingCustomer,
        fetching: fetchingExistingCustomer,
    } = responseExistingCustomer

    const addToCartExistingCustomer = () => {
        const input = getInput(RingBuilderResolveMode.AddToCart)

        if (!input) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationExistingCustomer({
            input,
            locale: locale.value,
            country: countryCode.value!,
            currency: currency.value,
        }).then(({ error, data: response }) => {
            const data = response?.ringBuilderResolveExistingCustomer

            if (!error && data?.__typename !== 'RingBuilderResolvedFailure') {
                handleAddToCartSuccess()

                authForm.value?.handleRegistrationResponse?.(undefined, {
                    name: ROUTE_CART,
                })
            }
        })
    }

    // New customer
    const responseNewCustomer = useRingBuilderResolveNewCustomerMutation()

    const {
        executeMutation: mutationNewCustomer,
        fetching: fetchingNewCustomer,
    } = responseNewCustomer

    const addToCartNewCustomer = (successCallback: () => void) => {
        const input = getInput(RingBuilderResolveMode.AddToCart)
        const customer = customerRegistrationInput.value

        if (!input || !customer) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationNewCustomer({
            input,
            customer,
            locale: locale.value,
            country: countryCode.value!,
            currency: currency.value,
            isConsultant: isAdmin.value,
        }).then(({ error, data: response }) => {
            const data = response?.ringBuilderResolveNewCustomer

            if (!error && data?.__typename !== 'RingBuilderResolvedFailure') {
                handleAddToCartSuccess()

                if (data?.customer.login) {
                    successCallback()
                }

                authForm.value?.handleRegistrationResponse?.(data?.customer, {
                    name: ROUTE_CART,
                })
            }
        })
    }

    const addToCartErrors = [
        createResponseAndPath(
            responseExistingCustomer,
            'ringBuilderResolveExistingCustomer'
        ),
        createResponseAndPath(
            responseNewCustomer,
            'ringBuilderResolveNewCustomer'
        ),
    ] as const

    const fetchingAddToCart = computed(
        () => fetchingExistingCustomer.value || fetchingNewCustomer.value
    )

    return {
        addToCartNewCustomer,
        addToCartErrors,
        addToCartExistingCustomer,
        fetchingAddToCart,
    }
}
